import type { RefObject } from 'react';
import { LoadingSpinner, QueryErrorAlert } from '@shared/components';
import { SubmittedInvoiceList } from './SubmittedInvoiceList';
import type { AgreementInvoice } from '../types/agreements.types';

interface Props {
    invoices: AgreementInvoice[] | undefined;
    isLoading: boolean;
    isError: boolean;
    refetch: () => unknown;
    dealerNames: Map<string, string>;
    headingRef: RefObject<HTMLHeadingElement | null>;
    canReverse?: (invoice: AgreementInvoice) => boolean;
    onReverse?: (invoice: AgreementInvoice) => void;
}

/** Left column of the Previously invoiced screen, inside its `invoice.browse` guard.
 *  `headingRef` receives focus when the user switches to this screen. */
export function SubmittedInvoicesSection({ invoices, isLoading, isError, refetch, dealerNames, headingRef, canReverse, onReverse }: Props) {
    const hasInvoices = !isLoading && !isError && invoices !== undefined && invoices.length > 0;

    return (
        <section aria-labelledby="submitted-invoices-heading" className="min-w-0 space-y-3">
            <h4
                ref={headingRef}
                id="submitted-invoices-heading"
                tabIndex={-1}
                className="text-sm font-semibold text-mp-foreground focus-visible:outline-hidden focus-visible:ring-3 focus-visible:ring-mp-primary"
            >
                Previously invoiced
            </h4>

            {isLoading && <LoadingSpinner message="Loading previously invoiced" className="p-4" />}

            {!isLoading && isError && (
                <QueryErrorAlert message="Failed to load previously invoiced." onRetry={() => refetch()} />
            )}

            {!isLoading && !isError && !hasInvoices && (
                <p className="text-sm text-mp-muted">Nothing has been previously invoiced yet.</p>
            )}

            {hasInvoices && (
                <SubmittedInvoiceList
                    invoices={invoices}
                    dealerNames={dealerNames}
                    canReverse={canReverse}
                    onReverse={onReverse}
                />
            )}
        </section>
    );
}
